import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { platformSalesShippingApi } from '../../api/platformSalesShipping';
import { message } from 'antd';

// 获取平台代售物流信息列表
export const fetchPlatformSalesShippingList = createAsyncThunk(
  'platformSalesShipping/fetchList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await platformSalesShippingApi.getPlatformSalesShippingList(params);
      // 后端返回的是 IPage 格式，包含 records, total, current, size 等字段
      const data = response.data || response;
      return {
        list: data.records || data.list || [],
        total: data.total || 0,
        current: data.current || params.page || 1,
        pageSize: data.size || params.size || 10
      };
    } catch (error) {
      console.error('获取平台代售物流信息列表失败:', error);
      return rejectWithValue(error.response?.data || { message: '获取平台代售物流信息列表失败' });
    }
  }
);

// 获取平台代售物流信息详情
export const fetchPlatformSalesShippingDetail = createAsyncThunk(
  'platformSalesShipping/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await platformSalesShippingApi.getPlatformSalesShippingDetail(id);
      return response.data || response;
    } catch (error) {
      console.error('获取平台代售物流信息详情失败:', error);
      return rejectWithValue(error.response?.data || { message: '获取平台代售物流信息详情失败' });
    }
  }
);

// 创建平台代售物流信息
export const createPlatformSalesShipping = createAsyncThunk(
  'platformSalesShipping/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await platformSalesShippingApi.createPlatformSalesShipping(data);
      message.success('创建物流信息成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '创建物流信息失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 更新平台代售物流信息
export const updatePlatformSalesShipping = createAsyncThunk(
  'platformSalesShipping/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await platformSalesShippingApi.updatePlatformSalesShipping(id, data);
      message.success('更新物流信息成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '更新物流信息失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 删除平台代售物流信息
export const deletePlatformSalesShipping = createAsyncThunk(
  'platformSalesShipping/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await platformSalesShippingApi.deletePlatformSalesShipping(id);
      message.success('删除物流信息成功');
      return id;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '删除物流信息失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 根据订单ID查询物流信息
export const fetchShippingByOrderId = createAsyncThunk(
  'platformSalesShipping/fetchByOrderId',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await platformSalesShippingApi.getShippingByOrderId(orderId);
      return response.data || response;
    } catch (error) {
      console.error('根据订单ID查询物流信息失败:', error);
      return rejectWithValue(error.response?.data || { message: '根据订单ID查询物流信息失败' });
    }
  }
);

// 平台代售物流信息 Slice
const platformSalesShippingSlice = createSlice({
  name: 'platformSalesShipping',
  initialState: {
    list: [],
    currentShipping: null,
    shippingByOrder: null,
    loading: false,
    error: null,
    pagination: {
      total: 0,
      current: 1,
      pageSize: 10
    }
  },
  reducers: {
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearCurrentShipping: (state) => {
      state.currentShipping = null;
    },
    clearShippingByOrder: (state) => {
      state.shippingByOrder = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchPlatformSalesShippingList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlatformSalesShippingList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.list;
        state.pagination.total = action.payload.total;
        state.pagination.current = action.payload.current;
        state.pagination.pageSize = action.payload.pageSize;
      })
      .addCase(fetchPlatformSalesShippingList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 获取详情
      .addCase(fetchPlatformSalesShippingDetail.fulfilled, (state, action) => {
        state.currentShipping = action.payload;
      })
      
      // 创建物流信息
      .addCase(createPlatformSalesShipping.fulfilled, (state, action) => {
        state.list.unshift(action.payload);
        state.pagination.total += 1;
      })
      
      // 更新物流信息
      .addCase(updatePlatformSalesShipping.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentShipping && state.currentShipping.id === action.payload.id) {
          state.currentShipping = action.payload;
        }
      })
      
      // 删除物流信息
      .addCase(deletePlatformSalesShipping.fulfilled, (state, action) => {
        state.list = state.list.filter(item => item.id !== action.payload);
        state.pagination.total -= 1;
        if (state.currentShipping && state.currentShipping.id === action.payload) {
          state.currentShipping = null;
        }
      })
      
      // 根据订单ID查询物流信息
      .addCase(fetchShippingByOrderId.fulfilled, (state, action) => {
        state.shippingByOrder = action.payload;
      });
  }
});

export const { setPagination, clearCurrentShipping, clearShippingByOrder } = platformSalesShippingSlice.actions;
export default platformSalesShippingSlice.reducer;

